import React from 'react'
import {
  MessageCard,
  ListGroup,
  ListGroupItem,
  Icon,
  Badge as ComponentsBadge,
} from '@pandiafi/components'
import { Badge, BadgeColor } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'
import { DateTime } from 'luxon'

import { MessageThread } from '../../../common/validations/residentMessageTimelineSchema'
import { SanitizedHtml } from '../../components/SanitizedHtml/SanitizedHtml'
import { getMessageCategoryColor } from '../../utils/messageCategoryColor'
import { isNewMessage } from '../../utils/newMessage'

export const MessageThreadCard = ({
  msg,
  latestMessageId,
  threadId,
  onViewThread,
  contentHeight = 'default',
  header,
  categoryName,
}: {
  msg: MessageThread['entries'][number]
  latestMessageId: number
  threadId: string
  onViewThread: ((id: string) => void) | null
  contentHeight?: 'short' | 'default'
  header?: React.ReactNode
  categoryName?: string | null
}) => {
  const { t, i18n } = useTranslation(['messages'])

  const shouldShowNewBadge =
    msg.messageId === latestMessageId && isNewMessage(msg)

  return (
    <MessageCard
      key={`msg-${msg.messageId}`}
      variant={msg.__typename === 'IncomingEmailMessage' ? 'sent' : 'received'}
      contentHeight={contentHeight}
      header={header}
      accentColor={getMessageCategoryColor(categoryName)}
      title={
        <span className="flex flex-wrap items-center gap-2">
          <span>{msg.title}</span>
          {shouldShowNewBadge && (
            <Badge color={BadgeColor.PRIMARY}>
              {t('messages:view.newBadge')}
            </Badge>
          )}
        </span>
      }
      footerLeft={
        msg.__typename !== 'ManualEmailConversation' && msg.senderName ? (
          <ComponentsBadge
            size="s"
            color="var(--common-gray-7)"
            backgroundColor="var(--common-gray-3)"
          >
            {msg.senderName}
          </ComponentsBadge>
        ) : null
      }
      footerRight={
        <span>
          {DateTime.fromISO(msg.timestamp)
            .setLocale(i18n.language)
            .toLocaleString(DateTime.DATETIME_SHORT)}
        </span>
      }
      drawer={
        msg.files && msg.files.length > 0 ? (
          <ListGroup>
            {msg.files.map((file) => (
              <ListGroupItem
                icon={<Icon variant="balloon" icon="paperclip" size="s" />}
                key={file.url}
                content={
                  <div className="overflow-hidden text-ellipsis">
                    {file.name}
                  </div>
                }
                onItemClick={() => {
                  window.open(file.url, '_blank')
                }}
              />
            ))}
          </ListGroup>
        ) : null
      }
      onClick={
        onViewThread
          ? () => {
              onViewThread(threadId)
            }
          : undefined
      }
    >
      <SanitizedHtml
        html={msg.htmlContent}
        className="prose max-w-none [&_a]:text-primary [&_a]:underline"
      />
    </MessageCard>
  )
}
